import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { usePageTitle } from "@/hooks/use-page-title";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/use-auth";
import {
  Wallet,
  Users,
  Heart,
  Building2,
  Truck,
  Thermometer,
  MapPin,
  Shield,
  Clock,
  CreditCard,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Snowflake,
  FileCheck,
  Globe,
  Gift,
  Quote,
  Stethoscope,
  Baby,
  Ribbon,
  Search,
  HandHeart,
  BarChart3,
  Briefcase,
  UserPlus,
  PiggyBank,
  HeartPulse,
  Activity,
  DollarSign,
  Zap,
} from "lucide-react";
import { featureFlags } from "@/lib/feature-flags";
import { Skeleton } from "@/components/ui/skeleton";
import { useSuccessStories } from "@/hooks/use-queries";
import { CommunityImpactSection } from "@/components/community-impact";
import { HeroAnimation } from "@/components/hero-animation";
import { VideoShowcase } from "@/components/video-showcase";
import { MarketingLayout } from "@/components/marketing-layout";

function ReferralBanner({ refCode }: { refCode: string | null }) {
  const { user } = useAuth();

  if (!refCode || user) return null;

  return (
    <div className="bg-primary text-primary-foreground py-3 px-4 text-center" data-testid="banner-referral">
      <div className="flex items-center justify-center gap-3 flex-wrap">
        <Gift className="w-5 h-5" />
        <span className="font-medium">You've been invited! Sign up now to receive a $10 welcome bonus.</span>
        <Link href="/auth?tab=register">
          <Button variant="secondary" size="sm" data-testid="button-referral-signup">
            Sign Up Now
          </Button>
        </Link>
      </div>
    </div>
  );
}

export default function LandingPage() {
  usePageTitle(undefined, "Pre-fund your healthcare through community-powered wallets. Serving small businesses in Minneapolis–St. Paul.");
  const [referralCode, setReferralCode] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refFromUrl = params.get('ref');
    const refFromStorage = sessionStorage.getItem('referralCode');
    const code = refFromUrl || refFromStorage;
    if (code) {
      sessionStorage.setItem('referralCode', code);
      setReferralCode(code);
    }
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <ReferralBanner refCode={referralCode} />
      <MarketingLayout hasHero>
        <HeroSection />
        <HowItWorksSection />
        <WalletBenefitsSection />
        <SuccessStoriesSection />
        <CommunityImpactSection variant="landing" />
        {featureFlags.medicationDelivery && <DeliveryFeaturesSection />}
        <SmallBusinessSection />
        <TrustSection />
        <FinalCTASection />
      </MarketingLayout>
    </div>
  );
}

function HeroSection() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();

  const handleWalletCreate = () => {
    if (user) {
      setLocation("/wallet/create");
    } else {
      setLocation("/auth");
    }
  };

  return (
    <section className="relative min-h-[480px] sm:min-h-[600px] flex items-center justify-center overflow-hidden -mt-16">
      <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/90 to-primary/80" />
      <HeroAnimation />
      
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-20 text-center">
        <Badge variant="secondary" className="mb-6 px-4 py-2 text-sm font-medium bg-white/20 text-white border-white/30 backdrop-blur-sm">
          <Heart className="w-4 h-4 mr-2 inline" />
          Serving Minneapolis-St. Paul Communities
        </Badge>
        
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
          Where Community Meets Care
        </h1>
        
        <p className="text-lg sm:text-xl md:text-2xl text-white/90 mb-4 sm:mb-6 font-medium">
          Pre-funded care. Community support. Peace of mind.
        </p>
        
        <p className="text-base sm:text-lg md:text-xl text-white/80 mb-8 sm:mb-10 max-w-3xl mx-auto leading-relaxed">
          Patients build health wallets with community support. Providers access pre-funded accounts for guaranteed payments. Everyone wins when healthcare is funded before the bill arrives.
        </p>
        
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button size="lg" className="bg-white text-primary font-semibold shadow-lg" data-testid="button-create-wallet-hero" onClick={handleWalletCreate}>
            <Wallet className="w-5 h-5 mr-2" />
            {user ? "Start Your HealthWallet" : "Get Started"}
          </Button>
          <Link href="/conditions">
            <Button size="lg" className="bg-emerald-400 dark:bg-emerald-500 text-emerald-900 font-semibold shadow-lg" data-testid="button-explore-conditions-hero">
              <Heart className="w-5 h-5 mr-2" />
              Browse by Condition
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
          <a href="#how-it-works">
            <Button size="lg" variant="outline" className="border-white/40 text-white bg-white/10 backdrop-blur-sm" data-testid="button-learn-more-hero">
              See How It Works
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>

        <div className="mt-10 sm:mt-16 flex flex-wrap items-center justify-center gap-4 sm:gap-8 text-white/70">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            <span className="text-sm font-medium">Bank-Level Security</span>
          </div>
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            <span className="text-sm font-medium">4 Languages Supported</span>
          </div>
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5" />
            <span className="text-sm font-medium">Community Powered</span>
          </div>
        </div>
      </div>
    </section>
  );
}

type PersonaKey = "patients" | "contributors" | "hospitals" | "employers";

const personaTabs: Array<{ key: PersonaKey; label: string; icon: typeof Wallet; description: string }> = [
  { key: "patients", label: "Patients & Families", icon: Heart, description: "From wallet creation to paying for care, we've made healthcare funding simple and secure." },
  { key: "contributors", label: "Contributors", icon: HandHeart, description: "Make an impact by funding healthcare for individuals and communities who need it most." },
  { key: "hospitals", label: "Hospitals", icon: Stethoscope, description: "Accelerate revenue collection and reduce bad debt with pre-funded patient wallets." },
  { key: "employers", label: "Employers", icon: Briefcase, description: "Support employee wellness at scale with funded health wallets and matching contributions." },
];

const personaSteps: Record<PersonaKey, Array<{ icon: typeof Wallet; title: string; description: string; color: string }>> = {
  patients: [
    { icon: Wallet, title: "Create Your Wallet", description: "Set up an Individual, Family, Community, or Organization health wallet in minutes.", color: "bg-primary" },
    { icon: Users, title: "Receive Contributions", description: "Share your wallet link with family, friends, and community members who want to help.", color: "bg-[#E07856]" },
    { icon: Heart, title: "Build Your Fund", description: "Watch your community rally around your healthcare needs with transparent progress tracking.", color: "bg-emerald-500" },
    { icon: CreditCard, title: "Pay for Care", description: "Use your pre-funded wallet to pay providers directly or set up flexible payment schedules through our hospital partners.", color: "bg-primary" },
  ],
  contributors: [
    { icon: Search, title: "Browse Community Funds", description: "Explore condition-based funds for causes like diabetes, cancer, pediatric care, and more.", color: "bg-primary" },
    { icon: HeartPulse, title: "Choose a Cause", description: "Select a community fund or support a specific individual's healthcare journey.", color: "bg-[#E07856]" },
    { icon: CreditCard, title: "Contribute Securely", description: "Make one-time or recurring contributions through our secure Stripe-powered payment system.", color: "bg-emerald-500" },
    { icon: BarChart3, title: "Track Your Impact", description: "See transparent reporting on how your contributions are helping real patients get care.", color: "bg-primary" },
  ],
  hospitals: [
    { icon: FileCheck, title: "Apply for Partnership", description: "Submit your facility information and join our network of hospital partners.", color: "bg-primary" },
    { icon: Wallet, title: "Access Patient Wallets", description: "Connect to pre-funded patient wallets for faster, guaranteed payment collection.", color: "bg-[#E07856]" },
    { icon: TrendingUp, title: "Accelerate Payments", description: "Reduce days-to-payment and bad debt with community-backed funding already in place.", color: "bg-emerald-500" },
    { icon: BarChart3, title: "Track ROI", description: "Monitor revenue cycle improvements with detailed KPI dashboards and downloadable reports.", color: "bg-primary" },
  ],
  employers: [
    { icon: Briefcase, title: "Enroll Your Company", description: "Register as an employer partner and choose a program tier that fits your team.", color: "bg-primary" },
    { icon: UserPlus, title: "Add Employees", description: "Invite employees via company codes so they can link their health wallets.", color: "bg-[#E07856]" },
    { icon: PiggyBank, title: "Fund Health Wallets", description: "Contribute one-time, recurring, or matching funds to employee health wallets at scale.", color: "bg-emerald-500" },
    { icon: HeartPulse, title: "Improve Wellness", description: "Reduce absenteeism and healthcare burden while showing employees you care.", color: "bg-primary" },
  ],
};

function HowItWorksSection() {
  const [activePersona, setActivePersona] = useState<PersonaKey>("patients");
  const steps = personaSteps[activePersona];
  const activeTab = personaTabs.find(t => t.key === activePersona)!;

  return (
    <section id="how-it-works" className="py-12 sm:py-20 px-4 sm:px-6 bg-muted/30">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8 sm:mb-10">
          <Badge variant="outline" className="mb-4">Simple Process</Badge>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">How It Works</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {activeTab.description}
          </p>
        </div>

        <div className="mb-8 sm:mb-12 max-w-3xl mx-auto">
          <VideoShowcase />
        </div>

        <div className="flex flex-wrap justify-center gap-2 mb-8 sm:mb-12" data-testid="persona-tabs">
          {personaTabs.map((tab) => {
            const TabIcon = tab.icon;
            const isActive = activePersona === tab.key;
            return (
              <Button
                key={tab.key}
                variant={isActive ? "default" : "outline"}
                onClick={() => setActivePersona(tab.key)}
                data-testid={`tab-${tab.key}`}
              >
                <TabIcon className="w-4 h-4 mr-2" />
                {tab.label}
              </Button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((item, index) => (
            <div key={`${activePersona}-${index}`} className="relative">
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-12 left-[60%] w-[80%] h-0.5 bg-border" />
              )}
              <Card className="relative bg-card hover-elevate transition-all duration-300">
                <CardContent className="pt-8 pb-6 px-6 text-center">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${item.color} text-white mb-6`}>
                    <item.icon className="w-7 h-7" />
                  </div>
                  <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-muted flex items-center justify-center text-sm font-bold text-muted-foreground">
                    {index + 1}
                  </div>
                  <h3 className="text-xl font-semibold mb-3" data-testid={`step-title-${index}`}>{item.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>

        {activePersona === "employers" && (
          <div className="mt-8 text-center" data-testid="section-employer-cta">
            <Link href="/small-business/signup">
              <Button size="lg" data-testid="button-employer-get-started">
                <Briefcase className="w-5 h-5 mr-2" />
                Get Started as a Business Owner
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <p className="text-sm text-muted-foreground mt-3">Free to set up. No contracts. Cancel anytime.</p>
          </div>
        )}
      </div>
    </section>
  );
}

function WalletBenefitsSection() {
  const walletTypes = [
    {
      icon: Wallet,
      title: "Individual Wallet",
      description: "Your personal healthcare fund. Save, receive community contributions, and pay providers directly.",
      features: ["Shareable funding link", "Community contributions", "Flexible payment schedules"],
    },
    {
      icon: Users,
      title: "Family Wallet",
      description: "Cover your whole family under one fund with shared balances and multiple beneficiaries.",
      features: ["Multiple beneficiaries", "Shared family balance", "Spouse & children funding"],
    },
    {
      icon: Heart,
      title: "Community Wallet",
      description: "Neighbors and community members pooling resources for collective healthcare support.",
      features: ["Crowdfunded contributions", "Condition-based funds", "Neighborhood health support"],
    },
    {
      icon: Ribbon,
      title: "Condition Wallet",
      description: "Dedicated funds organized by medical condition, connecting patients with community support.",
      features: ["13 pediatric specialties", "Public contributor support", "Patient membership enrollment"],
    },
    {
      icon: Building2,
      title: "Organization Wallet",
      description: "For churches, mosques, nonprofits, and cooperatives funding healthcare for their members.",
      features: ["Verified organization status", "Multi-admin management", "Tax-ID linked funding"],
    },
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: "Flexible Payment Schedules",
      description: "Split your healthcare costs into manageable payments facilitated by your provider.",
    },
    {
      icon: CreditCard,
      title: "Pre-Care Subscriptions",
      description: "Set up recurring contributions to build your health fund over time.",
    },
    {
      icon: Heart,
      title: "Community Crowdfunding",
      description: "Share your wallet link and receive contributions from those who care.",
    },
    {
      icon: CheckCircle,
      title: "Direct Provider Payments",
      description: "Pay healthcare providers directly from your wallet for seamless transactions.",
    },
  ];

  return (
    <section className="py-12 sm:py-20 px-4 sm:px-6">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10 sm:mb-16">
          <Badge variant="outline" className="mb-4 border-accent text-accent">
            <Wallet className="w-4 h-4 mr-2" />
            HealthWallet
          </Badge>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">Your Healthcare, Your Way</h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
            Choose the wallet type that fits your needs and let your community support your health journey.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-6 mb-10 sm:mb-16">
          {walletTypes.map((wallet, index) => (
            <Card key={index} className="bg-gradient-to-b from-card to-card/80 hover-elevate transition-all duration-300 border-2 hover:border-primary/50">
              <CardContent className="pt-6 sm:pt-8 pb-5 sm:pb-6 px-4 sm:px-6">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10 text-primary mb-5">
                  <wallet.icon className="w-7 h-7" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{wallet.title}</h3>
                <p className="text-muted-foreground text-sm mb-4 leading-relaxed">{wallet.description}</p>
                <ul className="space-y-2">
                  {wallet.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center text-sm text-muted-foreground">
                      <CheckCircle className="w-4 h-4 mr-2 text-emerald-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="bg-muted/50 rounded-2xl p-5 sm:p-8 md:p-12">
          <h3 className="text-xl sm:text-2xl font-bold text-center mb-6 sm:mb-10">Additional Benefits</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                  <benefit.icon className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h4 className="font-semibold mb-1">{benefit.title}</h4>
                  <p className="text-muted-foreground text-sm">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function SuccessStoriesSection() {
  const { data: stories = [], isLoading } = useSuccessStories();

  const iconMap: Record<string, any> = {
    stethoscope: Stethoscope,
    heart: Heart,
    users: Users,
    activity: Activity,
    shield: Shield,
    zap: HeartPulse,
  };

  if (!isLoading && stories.length === 0) return null;

  return (
    <section className="py-12 sm:py-20 px-4 sm:px-6 bg-primary/5" data-testid="section-success-stories">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10 sm:mb-16">
          <Badge variant="outline" className="mb-4 border-accent text-accent">
            <Heart className="w-4 h-4 mr-2" />
            Real Impact
          </Badge>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4" data-testid="text-success-stories-title">
            Stories From Our Community
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
            Real journeys from patients and families in the Twin Cities who funded their care through community support.
          </p>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {[0, 1, 2, 3].map(i => (
              <Card key={i}>
                <CardContent className="p-4 sm:p-6 space-y-4">
                  <Skeleton className="h-12 w-3/4" />
                  <Skeleton className="h-20 w-full" />
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-12 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {stories.map((story, index) => {
              const IconComponent = iconMap[story.iconType] || Stethoscope;
              return (
                <Card key={story.id} className="hover-elevate transition-all duration-300" data-testid={`card-story-${index}`}>
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <IconComponent className="w-6 h-6 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-lg" data-testid={`text-story-name-${index}`}>{story.name}</h3>
                          <span className="text-sm text-muted-foreground">{story.location}</span>
                        </div>
                        <Badge variant="secondary" className={`mt-1 text-xs font-medium ${story.conditionColor}`}>
                          {story.condition}
                        </Badge>
                      </div>
                    </div>

                    <div className="relative pl-4 mb-5">
                      <Quote className="absolute -left-1 -top-1 w-5 h-5 text-primary/30" />
                      <p className="text-sm text-muted-foreground leading-relaxed italic" data-testid={`text-story-quote-${index}`}>
                        {story.quote}
                      </p>
                    </div>

                    <div className="bg-muted/50 rounded-lg p-4 mb-4">
                      <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">Care Accessed</p>
                      <p className="text-sm" data-testid={`text-story-care-${index}`}>{story.careAccessed}</p>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center p-2 rounded-lg bg-emerald-500/10">
                        <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400" data-testid={`text-story-funds-${index}`}>
                          {story.fundsRaised}
                        </div>
                        <div className="text-xs text-muted-foreground">Funded</div>
                      </div>
                      <div className="text-center p-2 rounded-lg bg-blue-500/10">
                        <div className="text-lg font-bold text-blue-600 dark:text-blue-400" data-testid={`text-story-contributors-${index}`}>
                          {story.contributors}
                        </div>
                        <div className="text-xs text-muted-foreground">Contributors</div>
                      </div>
                      <div className="text-center p-2 rounded-lg bg-primary/10">
                        <div className="text-sm font-bold text-primary" data-testid={`text-story-wallet-${index}`}>
                          {story.walletType}
                        </div>
                        <div className="text-xs text-muted-foreground">{story.timeframe}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <div className="text-center mt-12">
          <Link href="/conditions">
            <Button variant="outline" size="lg" data-testid="button-explore-conditions">
              Explore Condition Funds
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

function DeliveryFeaturesSection() {
  const features = [
    {
      icon: Thermometer,
      title: "Temperature Monitoring",
      description: "Real-time temperature tracking for sensitive medications with automatic alerts.",
      highlight: "Specialty Medications",
    },
    {
      icon: MapPin,
      title: "Live GPS Tracking",
      description: "Track your delivery in real-time from pharmacy to your doorstep.",
      highlight: "Real-Time Updates",
    },
    {
      icon: Snowflake,
      title: "Cold Chain Compliance",
      description: "Maintaining proper storage conditions throughout the entire delivery journey.",
      highlight: "Quality Assured",
    },
    {
      icon: FileCheck,
      title: "Chain of Custody",
      description: "Complete audit trail and documentation for every delivery.",
      highlight: "Bank-Level Security",
    },
    {
      icon: Clock,
      title: "Scheduled Delivery",
      description: "Choose delivery times that work for your schedule.",
      highlight: "Your Convenience",
    },
    {
      icon: Shield,
      title: "Secure Handling",
      description: "Professional drivers trained in healthcare logistics and patient privacy.",
      highlight: "Trusted Delivery",
    },
  ];

  return (
    <section className="py-20 px-6 bg-primary/5">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <Badge variant="outline" className="mb-4">
            <Truck className="w-4 h-4 mr-2" />
            Specialty Medication Delivery
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Delivered With Care</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Your medications are handled with the highest standards of safety and compliance.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <Card key={index} className="bg-card hover-elevate transition-all duration-300">
              <CardContent className="pt-6 pb-6 px-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <Badge variant="secondary" className="mb-2 text-xs">{feature.highlight}</Badge>
                    <h3 className="font-semibold mb-1">{feature.title}</h3>
                    <p className="text-muted-foreground text-sm">{feature.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function TrustSection() {
  const stats = [
    { value: "4", label: "Languages Supported" },
  ];

  const compliance = [
    { icon: Shield, label: "Bank-Level Security" },
    { icon: FileCheck, label: "Full Audit Trail" },
    { icon: CreditCard, label: "Stripe Secured Payments" },
    { icon: CheckCircle, label: "Transparent Funding" },
  ];

  return (
    <section className="py-12 sm:py-20 px-4 sm:px-6">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10 sm:mb-16">
          <Badge variant="outline" className="mb-4">Trust & Compliance</Badge>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">Built on Trust</h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
            We take healthcare seriously. Our platform meets the highest standards of security and compliance.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:gap-6 mb-8 sm:mb-12 max-w-xs mx-auto">
          {stats.map((stat, index) => (
            <div key={index} className="text-center p-4 sm:p-6 rounded-xl bg-muted/50">
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary mb-2">{stat.value}</div>
              <div className="text-xs sm:text-sm text-muted-foreground">{stat.label}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-8">
          {compliance.map((item, index) => (
            <div key={index} className="flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-2 sm:py-3 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SmallBusinessSection() {
  const [, setLocation] = useLocation();

  const benefits = [
    { icon: DollarSign, title: "Start at Any Budget", description: "Contribute as little as your business can afford. No minimum per-employee amounts required." },
    { icon: Users, title: "No Minimum Group Size", description: "Whether you have 2 employees or 99, every team qualifies for health wallet benefits." },
    { icon: Zap, title: "No Waiting Periods", description: "Employees get immediate access to their health wallet funds from day one." },
    { icon: Heart, title: "Community Support", description: "Employees can receive additional funding from family, friends, and condition-based community funds." },
    { icon: CreditCard, title: "Flexible Payment Schedules", description: "When wallet funds run out, employees can arrange flexible payment schedules directly with partner hospitals." },
    { icon: Building2, title: "Hospital Network", description: "125+ healthcare providers across the Twin Cities accept HealthWallet for direct payments." },
  ];

  return (
    <section className="py-12 sm:py-20 px-4 sm:px-6 bg-muted/30" data-testid="section-small-business">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10 sm:mb-14">
          <Badge variant="secondary" className="mb-4" data-testid="badge-small-business">
            <Briefcase className="w-3.5 h-3.5 mr-1.5" />
            For Small Businesses
          </Badge>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4" data-testid="heading-small-business">
            Health Benefits Your Small Business Can Actually Afford
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto" data-testid="text-small-business-description">
            Traditional health insurance is out of reach for most small businesses. HealthWallet gives you a simpler way to support your team's health — no brokers, no minimums, no complexity.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-10 sm:mb-14">
          {benefits.map((benefit) => (
            <Card key={benefit.title} className="hover-elevate" data-testid={`card-benefit-${benefit.title.toLowerCase().replace(/\s+/g, '-')}`}>
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-start gap-4">
                  <div className="p-2.5 rounded-md bg-primary/10 shrink-0">
                    <benefit.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{benefit.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{benefit.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="overflow-visible" data-testid="card-small-business-cta">
          <CardContent className="p-6 sm:p-10">
            <div className="flex flex-col lg:flex-row items-center gap-6 lg:gap-10">
              <div className="flex-1 text-center lg:text-left">
                <h3 className="text-xl sm:text-2xl font-bold mb-3" data-testid="heading-small-business-cta">
                  Get Your Team Covered in Minutes
                </h3>
                <p className="text-muted-foreground mb-2" data-testid="text-small-business-cta">
                  Sign up takes less than 5 minutes. Choose a contribution plan, invite your employees, and start funding their health wallets right away.
                </p>
                <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 text-sm text-muted-foreground mt-4">
                  <span className="flex items-center gap-1.5" data-testid="text-free-setup">
                    <CheckCircle className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                    Free to set up
                  </span>
                  <span className="flex items-center gap-1.5" data-testid="text-no-contracts">
                    <CheckCircle className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                    No contracts
                  </span>
                  <span className="flex items-center gap-1.5" data-testid="text-cancel-anytime">
                    <CheckCircle className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                    Cancel anytime
                  </span>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button size="lg" data-testid="button-small-business-signup" onClick={() => setLocation("/small-business/signup")}>
                  <Briefcase className="w-5 h-5 mr-2" />
                  Start Free Signup
                </Button>
                <Button size="lg" variant="outline" data-testid="button-small-business-learn" onClick={() => {
                  const el = document.getElementById("how-it-works");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                  setTimeout(() => {
                    const tab = document.querySelector('[data-testid="tab-employers"]') as HTMLElement;
                    if (tab) tab.click();
                  }, 500);
                }}>
                  Learn More
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

function FinalCTASection() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();

  const handleWalletCreate = () => {
    if (user) {
      setLocation("/wallet/create");
    } else {
      setLocation("/auth");
    }
  };

  return (
    <section className="py-12 sm:py-20 px-4 sm:px-6 bg-gradient-to-br from-primary via-primary/95 to-primary/90">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">
          Start Your Health Journey Today
        </h2>
        <p className="text-base sm:text-xl text-white/80 mb-8 sm:mb-10 max-w-2xl mx-auto">
          Join thousands of families in the Minneapolis-St. Paul area who are taking control of their healthcare costs with community support.
        </p>
        
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button size="lg" className="bg-white text-primary font-semibold shadow-lg" data-testid="button-create-wallet-cta" onClick={handleWalletCreate}>
            <Wallet className="w-5 h-5 mr-2" />
            {user ? "Start Your HealthWallet" : "Get Started"}
          </Button>
          <Link href="/community-funds">
            <Button size="lg" variant="outline" className="border-white/40 text-white bg-white/10 backdrop-blur-sm" data-testid="button-explore-campaigns">
              Community Funds
            </Button>
          </Link>
        </div>

        <p className="mt-8 text-white/60 text-sm">
          Available in English, Spanish, Somali, and Hmong
        </p>
      </div>
    </section>
  );
}

